// src/features/tuition/components/online/SubtopicForm.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Autocomplete,
  Chip,
  FormHelperText,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { 
  createSubtopic, 
  updateSubtopic, 
  fetchSubtopicsForTopic,
  fetchTopicsForSubject
} from '../../services/subjectsService';

const SubtopicForm = ({ open, onClose, onSubmit, initialData, topic, subjectId, userId }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    preknowledge: '',
    skills: '',
    relatedTopics: [],
    relatedSubtopics: []
  });
  
  const [allTopics, setAllTopics] = useState([]);
  const [allSubtopics, setAllSubtopics] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableTopics, setAvailableTopics] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  useEffect(() => {
    if (open) {
      loadSubjects();
      
      if (initialData) {
        setFormData({
          name: initialData.name || '',
          description: initialData.description || '',
          preknowledge: initialData.preknowledge || '',
          skills: initialData.skills || '',
          relatedTopics: initialData.relatedTopics || [],
          relatedSubtopics: initialData.relatedSubtopics || [],
          subjectId: initialData.subjectId || (topic ? topic.subjectId : '') || subjectId || '',
          topicId: initialData.topicId || (topic ? topic.id : '') || ''
        });
        
        // If we have a subject ID, load topics for that subject
        const targetSubjectId = initialData.subjectId || (topic ? topic.subjectId : '') || subjectId;
        if (targetSubjectId) {
          loadTopicsForSubject(targetSubjectId);
        }
        
        // Load related data if topic is provided
        if (topic) {
          loadRelatedData();
        }
      } else {
        resetForm();
      }
    }
  }, [initialData, open, topic, subjectId]);

  const loadSubjects = async () => {
    try {
      // This would typically be an API call
      // For example: const subjects = await fetchSubjects(userId);
      
      // Mock data for demonstration
      const subjects = [
        { id: subjectId || "subject1", name: "Current Subject" },
        { id: "subject2", name: "Mathematics" },
        { id: "subject3", name: "Science" }
      ];
      
      setAvailableSubjects(subjects);
    } catch (error) {
      console.error("Error loading subjects:", error);
    }
  };
  
  // Add function to load topics for a subject
  const loadTopicsForSubject = async (subjectId) => {
    try {
      // This would typically be an API call
      // For example: const topics = await fetchTopicsForSubject(subjectId);
      
      // Mock data for demonstration
      const topics = [
        { id: "topic1", name: "Topic 1", subjectId },
        { id: "topic2", name: "Topic 2", subjectId },
        { id: "topic3", name: "Topic 3", subjectId }
      ];
      
      setAvailableTopics(topics);
    } catch (error) {
      console.error("Error loading topics:", error);
    }
  };
  
  const loadRelatedData = async () => {
    try {
      // Load all topics for subject
      const topics = await fetchTopicsForSubject(subjectId);
      setAllTopics(topics);
      
      // Load all subtopics for all topics
      const allSubtopicsData = [];
      
      for (const t of topics) {
        const subtopics = await fetchSubtopicsForTopic(t.id);
        
        // If editing, filter out the current subtopic
        const filteredSubtopics = initialData 
          ? subtopics.filter(s => s.id !== initialData.id)
          : subtopics;
          
        allSubtopicsData.push(...filteredSubtopics);
      }
      
      setAllSubtopics(allSubtopicsData);
    } catch (error) {
      console.error("Error loading related data:", error);
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      preknowledge: '',
      skills: '',
      relatedTopics: [],
      relatedSubtopics: [],
      subjectId: subjectId || '',
      topicId: topic ? topic.id : ''
    });
    setErrors({});
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const handleRelatedTopicsChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      relatedTopics: newValue
    }));
  };
  
  const handleRelatedSubtopicsChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      relatedSubtopics: newValue
    }));
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Subtopic name is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.subjectId) {
      newErrors.subjectId = 'Subject is required';
    }
    
    if (!formData.topicId) {
      newErrors.topicId = 'Topic is required';
    }
    
    setErrors(newErrors);
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubjectChange = (e) => {
    const subjectId = e.target.value;
    setFormData(prev => ({
      ...prev,
      subjectId,
      topicId: '' // Reset topic when subject changes
    }));
    
    if (subjectId) {
      loadTopicsForSubject(subjectId);
    } else {
      setAvailableTopics([]);
    }
    
    // Clear error when user selects
    if (errors.subjectId) {
      setErrors(prev => ({ ...prev, subjectId: '' }));
    }
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const subtopicData = {
        name: formData.name,
        description: formData.description,
        preknowledge: formData.preknowledge,
        skills: formData.skills,
        relatedTopics: formData.relatedTopics,
        relatedSubtopics: formData.relatedSubtopics,
        topicId: topic.id,
        subjectId,
        userId
      };
      
      if (initialData && initialData.id) {
        // Update existing subtopic
        await updateSubtopic(initialData.id, subtopicData);
        
        setNotification({
          open: true,
          message: 'Subtopic updated successfully!',
          severity: 'success'
        });
      } else {
        // Create new subtopic
        await createSubtopic(subtopicData);
        
        setNotification({
          open: true,
          message: 'Subtopic created successfully!',
          severity: 'success'
        });
      }
      
      // Call onSubmit callback
      if (onSubmit) {
        onSubmit(subtopicData);
      }
      
      resetForm();
      
    } catch (error) {
      console.error("Error saving subtopic:", error);
      setNotification({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };
  
  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? null : onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ 
          sx: { borderRadius: 2 } 
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              {initialData ? 'Edit Subtopic' : 'Add New Subtopic'}
            </Typography>
            <IconButton 
              edge="end" 
              color="inherit" 
              onClick={onClose}
              disabled={loading}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Topic: {topic?.name}
              </Typography>
            </Box>
            
            <TextField
              fullWidth
              label="Subtopic Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              error={!!errors.name}
              helperText={errors.name}
              disabled={loading}
              sx={{ mb: 3 }}
            />
            
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={4}
              required
              error={!!errors.description}
              helperText={errors.description}
              disabled={loading}
              sx={{ mb: 3 }}
            />

            <FormControl 
            fullWidth 
            error={!!errors.subjectId} 
            sx={{ mb: 3 }}
            disabled={loading || !!subjectId || !!topic}
            >
            <InputLabel>Subject</InputLabel>
            <Select
                name="subjectId"
                value={formData.subjectId}
                label="Subject"
                onChange={handleSubjectChange}
                required
            >
                <MenuItem value="">
                <em>Select a subject</em>
                </MenuItem>
                {availableSubjects.map((subject) => (
                <MenuItem key={subject.id} value={subject.id}>
                    {subject.name}
                </MenuItem>
                ))}
            </Select>
            {errors.subjectId && <FormHelperText>{errors.subjectId}</FormHelperText>}
            {(!!subjectId || !!topic) && <FormHelperText>Subject selected from context</FormHelperText>}
            </FormControl>

            {/* Always show topic selection, but disable it appropriately */}
            <FormControl 
            fullWidth 
            error={!!errors.topicId} 
            sx={{ mb: 3 }}
            disabled={loading || !formData.subjectId || !!topic}
            >
            <InputLabel>Topic</InputLabel>
            <Select
                name="topicId"
                value={formData.topicId}
                label="Topic"
                onChange={handleInputChange}
                required
            >
                <MenuItem value="">
                <em>Select a topic</em>
                </MenuItem>
                {availableTopics.map((topic) => (
                <MenuItem key={topic.id} value={topic.id}>
                    {topic.name}
                </MenuItem>
                ))}
            </Select>
            {errors.topicId && <FormHelperText>{errors.topicId}</FormHelperText>}
            {!!topic && <FormHelperText>Topic selected from context</FormHelperText>}
            </FormControl>

            {/* If topic is provided directly, show its name */}
            {topic && (
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                Using topic: {topic.name}
                </Typography>
            </Box>
            )}
            
            <TextField
              fullWidth
              label="Pre-knowledge (Optional)"
              name="preknowledge"
              value={formData.preknowledge}
              onChange={handleInputChange}
              multiline
              rows={2}
              placeholder="What should students already know before starting this subtopic?"
              disabled={loading}
              sx={{ mb: 3 }}
            />
            
            <TextField
              fullWidth
              label="Required Skills (Optional)"
              name="skills"
              value={formData.skills}
              onChange={handleInputChange}
              multiline
              rows={2}
              placeholder="What skills should students already have before starting this subtopic?"
              disabled={loading}
              sx={{ mb: 3 }}
            />
            
            <Autocomplete
              multiple
              options={allTopics}
              getOptionLabel={(option) => option.name}
              value={formData.relatedTopics}
              onChange={handleRelatedTopicsChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Related Topics (Optional)"
                  placeholder="Select related topics"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                  />
                ))
              }
              disabled={loading}
              sx={{ mb: 3 }}
            />
            
            <Autocomplete
              multiple
              options={allSubtopics}
              getOptionLabel={(option) => option.name}
              value={formData.relatedSubtopics}
              onChange={handleRelatedSubtopicsChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Related Subtopics (Optional)"
                  placeholder="Select related subtopics"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                  />
                ))
              }
              disabled={loading}
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={onClose} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
            sx={{
              bgcolor: THEME_COLORS.primary,
              '&:hover': { bgcolor: '#1e1c4e' }
            }}
          >
            {loading ? 'Saving...' : initialData ? 'Update Subtopic' : 'Add Subtopic'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SubtopicForm;